import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { SearchedInfo } from '../models/search-info';
import { Observable, of, Subject } from 'rxjs';
import { DnvLatLng, DnvMapInfo, SearchItem, SearchItemType } from 'dnv-lib';

import { ApplicationState, LocationCoordinate, PopulationState, SearchCoordinatesPayload, SearchLocation, ToolboxMailMergeState } from '../state-management/application/application.state';
// import { environment } from 'src/environments/environment';
import { createFeatureSelector, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';


export interface State {
  applicationReducer: ApplicationState;
}

export const getApplicationState = createFeatureSelector<ApplicationState>('applicationReducer');

@Injectable()
export class SearchService implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject();
  appSettings: any;
  toolboxMailMergeState: ToolboxMailMergeState;
  gisUrl = 'api/gis/';
  propertiesUrl = 'api/properties/';
  httpOptions = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    })

  };
  httpOptionsString = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    }),
    responseType: 'text' as const
  };

  constructor(
    private httpClient: HttpClient,
    private store: Store<State>
  ) {
    this.store.select(getApplicationState).pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((state: any) => {
      this.appSettings = state.appSettings;
      this.toolboxMailMergeState = state.toolboxMailMergeState;
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getGeoInfo(searchInfo: SearchedInfo): any {

    // const path = searchInfo.searchedItem.assetId ? 'asset' : (searchInfo.searchedItem.type === 2 ? 'street' : 'park');
    let path;
    const id = searchInfo.searchedItem.assetId ? searchInfo.searchedItem.assetId : encodeURIComponent(searchInfo.searchedItem.name);
    if (searchInfo.searchedItem.assetId && searchInfo.searchedItem.assetId.toLowerCase().indexOf('public') === -1) {  // not trail
      path = 'asset';
    } else if (searchInfo.searchedItem.type === SearchItemType.StreetName) {
      path = 'street';
    } else if (searchInfo.searchedItem.type === SearchItemType.Park) {
      path = 'park';
    } else if (searchInfo.searchedItem.type === SearchItemType.Building || searchInfo.searchedItem.type === SearchItemType.School) {
      path = 'building';
    } else if (searchInfo.searchedItem.type === SearchItemType.NonPublicTrail) {
      path = 'nonpublic-trail';
    } else if (searchInfo.searchedItem.type === SearchItemType.PublicTrail) {
      path = 'public-trail';
    }

    // let url = searchInfo.apisUrl.endsWith('/') ? searchInfo.apisUrl : searchInfo.apisUrl + '/';
    // url += `${path}/${id}`;

    const url = this.gisUrl + 'geoinfo/' + `${path}/?name=${id}`;

    return this.httpClient.get(url);
  }

  getEDGeoInfo(drawingNumber: string): any {
    const url = 'api/engdrawingsgeo/' + drawingNumber;
    return this.httpClient.get(url);
  }

  getSearchItems(url: string): Observable<SearchItem[]> {
    return this.httpClient.get<SearchItem[]>(url);
  }

  getLocationCoordinates(location: SearchCoordinatesPayload): Observable<LocationCoordinate> {
    // console.log(location);

    return this.httpClient.get<LocationCoordinate>(
      // 'https://webservices.dnvwebstage.cdnv.dnv.ca/dnv_search/api/geoinfo/street/W%20QUEENS%20RD',
      this.gisUrl + 'intersections/' + location.name,
      this.httpOptions
    );

  }

  // getLocationFromCoordinates(latLng: DnvMapInfo): Observable<SearchLocation[]> {
  //   return this.httpClient.post<any>(this.gisUrl, latLng, this.httpOptions);
  // }

  getDbLink(num: string, type: number): any {
    return this.httpClient.get<any>(this.gisUrl + 'pidFolio/?qryVal=' + num + '&type=' + type, this.httpOptions);
  }

  getShape(dblink: string): any {
    // tslint:disable-next-line: max-line-length

    const url = this.appSettings['environment.folioSearchApiEndpointUrl'];


    const query = this.appSettings['searchService.cadastral'] + '/query?where=DBLink=%27' + dblink + '%27&text=&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=Shape&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&returnDistinctValues=false&resultOffset=&resultRecordCount=&f=geojson';
    return this.httpClient.get<any>(url + query, this.httpOptions);
  }

  getAirSpaceDbLink(dbLink: string): any {
    return this.httpClient.get(this.propertiesUrl + 'isAirSpace/?dblink=' + dbLink, this.httpOptions);
  }

  getCCTV(assetId: string): any {
    // update URL
    return this.httpClient.get('api/getCCTVDataAsync?assetId=' + assetId, this.httpOptions);
  }

  getCCTVRelatedData(objectId: string): any {
    // update URL
    return this.httpClient.get('api/getCCTVRelatedDataAsync?objectId=' + objectId, this.httpOptions);
  }

  getServiceConnectionsByDbLink(dbLink: string): any {
    return this.httpClient.get(this.propertiesUrl + 'serviceconnections/' + dbLink, this.httpOptions);
  }

  getServiceConnectionsByLatLong(lat: number, lon: number): any {
    return this.httpClient.get(this.propertiesUrl + 'serviceconnections/?lat='
      + lat.toString() + '&lon=' + lon.toString(), this.httpOptions);
  }

  getAddressDbLink(dbLink: string): any {
    return this.httpClient.get(this.propertiesUrl + 'address/?dbLink=' + dbLink, this.httpOptions);
  }

  getMailMergeBufferGeometry(geometry: any, distance: number): any {

    const url = this.appSettings['environment.folioSearchApiEndpointUrl'];
    // Unbox the inner array if the geometry contains more than one ring [[[[],[],[]]],[[[],[],[]]]] -> [[[],[],[]],[[],[],[]]]
    if (geometry.length > 1) {
      geometry = geometry.map((g: any) => g[0]);
    }

    const query = this.appSettings['environment.bufferUtil'];
    const geometries = JSON.stringify(
      {
        geometryType: 'esriGeometryPolygon',
        geometries: [{
          rings: geometry
        }]
      }
    );
    const body: any = {
      geometries,
      inSR: 4326,
      outSR: 4326,
      bufferSR: 4326,
      distances: distance >= 0 ? distance : 0,
      unit: 9001,
      unionResults: true,
      geodesic: true,
      f: 'pjson'
    };

    return this.httpClient.post<any>(url + query, (new URLSearchParams(body)).toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
  }

  getMailLabels(toolboxMailMergeState: ToolboxMailMergeState): any {
    return this.httpClient.post(this.propertiesUrl + 'maillabels', this.formatBody(toolboxMailMergeState), this.httpOptions);
  }

  getMailLabelsCount(toolboxMailMergeState: ToolboxMailMergeState): any {
    return this.httpClient.post(this.propertiesUrl + 'maillabelscount', this.formatBody(toolboxMailMergeState), this.httpOptions);
  }

  formatBody(toolboxMailMergeState: ToolboxMailMergeState): any {
    return {
      links: toolboxMailMergeState.selectedParcels.map(p => p.url).join(','),
      toOwners: toolboxMailMergeState.isOwners,
      toOccupants: toolboxMailMergeState.isOccupants,
      preferOwnerName: toolboxMailMergeState.prefOwn,
      groupOwners: toolboxMailMergeState.groupOwners
    };
  }

  getCoordinatesByObjectId(queryUrl: string, objectId: number): any {
    // tslint:disable-next-line: max-line-length
    const query = queryUrl + '/query?objectIds=' + objectId + '&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=*&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=4326&having=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&queryByDistance=&returnExtentOnly=false&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=json';
    return this.httpClient.get<any>(query, this.httpOptions);
  }


  getCoordinatesByAssetId(queryUrl: string, assetId: number, assetField: string): any {
    // tslint:disable-next-line: max-line-length
    const query = queryUrl + '/query?where=' + assetField + '=\'' + assetId + '\'&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=*&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=4326&having=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&queryByDistance=&returnExtentOnly=false&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=json';
    return this.httpClient.get<any>(query, this.httpOptions);
  }

  GetCAD(): any {
    const url = 'api/getcad';
    return this.httpClient.get<any>(url, this.httpOptions);
  }

  GetOutstandingED(): any {
    const url = 'api/getoutstandinged';
    return this.httpClient.get<any>(url, this.httpOptions);
  }

  updateEngDraw(data: any, type: string): any {
    let url = 'api/updateengdraw';
    if (type === 'outstanding') {
      url = 'api/updateoutstanding';
    }
    return this.httpClient.post<any>(url, data, this.httpOptions);
  }
}
